import Vue from 'vue'
import VueApollo from 'vue-apollo'

import { InMemoryCache } from 'apollo-cache-inmemory'

import { split, ApolloLink, concat } from 'apollo-link'
import { HttpLink } from 'apollo-link-http'

import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from 'apollo-utilities'

import { ApolloClient } from 'apollo-client';

import { onError } from '@apollo/client/link/error';
import { EventBus } from '@/eventBus';

// Install the vue plugin
Vue.use(VueApollo)

// Name of the localStorage item
const AUTH_TOKEN = 'apollo-token'

const httpLink = new HttpLink({
    uri: process.env.VUE_APP_GRAPHQL_HTTP
});

const wsLink = new GraphQLWsLink(createClient({
    url: process.env.VUE_APP_GRAPHQL_WSS,
    options: {
        reconnect: true,
    },
    connectionParams: () => ({
        'access-token': localStorage.getItem('access-token') || '',
    }),
    retryAttempts: 20
}));

const splitLink = split(({ query }) => {
    const { kind, operation } = getMainDefinition(query)
    return kind === 'OperationDefinition' && operation === 'subscription'
}, wsLink, httpLink);   


const authMiddleware = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
        headers: {
            ...headers,
            'access-token': localStorage.getItem('access-token') || '',
        }
    }));
    return forward(operation);
});

const logoutLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors && graphQLErrors.length) {
        const [{ extensions }] = graphQLErrors;
        if (extensions && extensions.status === 500) {
            EventBus.$emit("failedServerResponse");
        }
    }

    if (!networkError) {
        return;
    }

    const statusCode = networkError.statusCode || networkError.status || networkError.response?.status;
    if (!statusCode) {
        return;
    }

    if ([401, 403, 404].includes(statusCode)) {
        EventBus.$emit("logout");
    }
});

const tempLink = concat(authMiddleware, splitLink);
  
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        GetAccionesLista: {
          keyArgs: false,
          merge(_, incoming) {
            return incoming;
          }
        },
        GetVersionesOpciones: {
          keyArgs: false,
          merge(_, incoming) {
            return incoming;
          }
        }
      }
    }
  }
});


// apollo-client 2 guarda el resultado de cada mutación en el cache con una clave hecha de
// sus variables, y no la borra nunca: una subida en base64 (imágenes, videos) queda retenida
// hasta recargar la página. Ninguna query lee esos resultados salvo los que traen `id`,
// que Apollo normaliza; esas mutaciones piden el cache con MUTACION_CON_CACHE (utils/gql.js).
const mutacionesSinCache = { mutate: { fetchPolicy: 'no-cache' } };

// Config
const defaultOptions = {
    // LocalStorage token
    tokenName: AUTH_TOKEN,
    // Enable Automatic Query persisting with Apollo Engine
    persisting: false,
    // Use websockets for everything (no HTTP)
    // You need to pass a `wsEndpoint` for this to work
    websocketsOnly: false,
    // Is being rendered on the server?
    ssr: false,

    // Override default apollo link
    // note: don't override httpLink here, specify httpLink options in the
    // httpLinkOptions property of defaultOptions.
    link: logoutLink.concat(tempLink), /* authLink */
    defaultHttpLink: false,
    defaultOptions: mutacionesSinCache,

    // Override default cache
    cache

    // Override the way the Authorization header is set
    // getAuth: (tokenName) => ...

    // Additional ApolloClient options
    // apollo: { ... }
    // Client local data (see apollo-link-state)
    // clientState: { resolvers: { ... }, defaults: { ... } }
}

export const apolloClient = new ApolloClient({ ...defaultOptions })

// apollo-client 2 guarda las variables de cada mutación en su mutationStore hasta un
// clearStore/resetStore, así que una subida retiene cada base64 enviado. Esto borra las
// mutaciones ya terminadas (API interna, pero apollo-client 2 ya no recibe cambios).
export function liberarMutacionesTerminadas(client = apolloClient) {
    const store = client.queryManager && client.queryManager.mutationStore && client.queryManager.mutationStore.store;
    if (!store) return;
    for (const id of Object.keys(store)) {
        if (!store[id].loading) delete store[id];
    }
}


// Create vue apollo provider
export const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
        $query: {
            loadingKey: 'loading',
            fetchPolicy: 'cache-and-network',
        },
    },
    // Había un watchLoading que sólo llevaba un contador global para imprimirlo
    // por consola. Al sacar el log, el contador quedó sin lectores, así que se
    // eliminó junto con él. Ver 'Smart Query > options > watchLoading' si algún
    // día hace falta un indicador de carga global de verdad.
    errorHandler(error) {
        // eslint-disable-next-line no-console
        console.error('Error en una smart query de Apollo:', error);
    },
})

// -------------- apollo client 2 --------------------

const httpLinkCC = new HttpLink({
    uri: process.env.VUE_APP_GRAPHQL_WSS_HTTP
});
const splitLinkCC = split(({ query }) => {
    const { kind, operation } = getMainDefinition(query)
    return kind === 'OperationDefinition' && operation === 'subscription'
}, wsLink, httpLinkCC);   

const tempLinkCC = concat(authMiddleware, splitLinkCC);
  
const defaultOptionsCC = {
    tokenName: AUTH_TOKEN,
    persisting: false,
    websocketsOnly: false,
    ssr: false,
    link: logoutLink.concat(tempLinkCC),
    defaultHttpLink: false,
    cache: new InMemoryCache({}),
    defaultOptions: mutacionesSinCache,
    connectToDevTools: true
};

export const apolloClientCC = new ApolloClient({ ...defaultOptionsCC });

